import { apiFetch } from '../../../shared/api/apiFetch'
import type { TeamMapPinDto } from './teamMapPinsApi'
import type { TeamPinFolderDto } from './teamPinFoldersApi'

/**
 * The signed-in hunter's OWN pin collection — what the app calls a private
 * collection. Same document shape as a team's pins and folders (the DTOs are
 * literally reused), differing only in who owns them, so the browser draws and
 * groups both with the same code.
 *
 * Neither route takes an owner id: the API resolves the owner from the caller's
 * token, so there is no way to ask for somebody else's private pins.
 */
interface ListResponse<T> {
  items: T[]
}

export async function listMyMapPins(token: string): Promise<TeamMapPinDto[]> {
  const r = await apiFetch<ListResponse<TeamMapPinDto>>('/clubhouse/me/map-pins/', { token })
  return r.items
}

export async function listMyPinFolders(token: string): Promise<TeamPinFolderDto[]> {
  const r = await apiFetch<ListResponse<TeamPinFolderDto>>('/clubhouse/me/map-pin-folders/', { token })
  return r.items
}

export async function createMyPinFolder(name: string, token: string): Promise<TeamPinFolderDto> {
  return apiFetch<TeamPinFolderDto>('/clubhouse/me/map-pin-folders/', {
    method: 'POST',
    body: JSON.stringify({ name }),
    token,
  })
}

export async function renameMyPinFolder(
  folderId: string,
  name: string,
  token: string,
): Promise<TeamPinFolderDto> {
  return apiFetch<TeamPinFolderDto>(`/clubhouse/me/map-pin-folders/${encodeURIComponent(folderId)}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
    token,
  })
}

export async function deleteMyPinFolder(folderId: string, token: string): Promise<void> {
  await apiFetch<undefined>(`/clubhouse/me/map-pin-folders/${encodeURIComponent(folderId)}`, {
    method: 'DELETE',
    token,
  })
}

/** Files one of my own pins, or takes it out of any folder when `folderId` is null. */
export async function fileMyMapPin(
  pinId: string,
  folderId: string | null,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`/clubhouse/me/map-pins/${encodeURIComponent(pinId)}/folder`, {
    method: 'PUT',
    body: JSON.stringify({ folderId }),
    token,
  })
}
